import type { Matrix } from "@babylonjs/core/Maths/math.vector";

/** One mesh of a kit frame at rest: its triangles and the matrix that takes them into the object root's space. */
export interface RestTriangles {
    positions: ArrayLike<number>;
    indices: ArrayLike<number>;
    toRoot: Matrix;
}

/**
 * The clear opening a frame's moulding leaves, in the root's meters: along the horizontal and vertical lines through
 * `centre`, the gap between the nearest triangle edge on either side. It reads the moulding itself, so rounded inner
 * corners and a fill face that is not the same size as the opening do not change it. Null when a side has no moulding.
 */
export function clearOpening(parts: readonly RestTriangles[], centre: { x: number; y: number }): { x: number; y: number } | null {
    let left = -Infinity, right = Infinity, below = -Infinity, above = Infinity;
    for (const { positions, indices, toRoot } of parts) {
        const m = toRoot.m;
        const x = new Float64Array(positions.length / 3);
        const y = new Float64Array(x.length);
        for (let i = 0; i < x.length; i++) {
            const px = positions[i * 3], py = positions[i * 3 + 1], pz = positions[i * 3 + 2];
            x[i] = m[0] * px + m[4] * py + m[8] * pz + m[12];
            y[i] = m[1] * px + m[5] * py + m[9] * pz + m[13];
        }
        for (let t = 0; t + 2 < indices.length; t += 3) {
            for (let e = 0; e < 3; e++) {
                const a = indices[t + e], b = indices[t + (e + 1) % 3];
                if ((y[a] - centre.y) * (y[b] - centre.y) <= 0 && y[a] !== y[b]) {
                    const at = x[a] + (x[b] - x[a]) * ((centre.y - y[a]) / (y[b] - y[a]));
                    if (at <= centre.x) left = Math.max(left, at); else right = Math.min(right, at);
                }
                if ((x[a] - centre.x) * (x[b] - centre.x) <= 0 && x[a] !== x[b]) {
                    const at = y[a] + (y[b] - y[a]) * ((centre.x - x[a]) / (x[b] - x[a]));
                    if (at <= centre.y) below = Math.max(below, at); else above = Math.min(above, at);
                }
            }
        }
    }
    return Number.isFinite(left) && Number.isFinite(right) && Number.isFinite(below) && Number.isFinite(above)
        ? { x: right - left, y: above - below }
        : null;
}
